import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import {
  LoadJobsiteSalesAreaComplete,
  LOAD_SALES_AREA_SUCCESS,
} from '@ordersnproducts/modules/order-builder/modules/location/actions/jobsite-actions/jobsite.actions';
import {
  PurchaseOrderMandatoryAction,
  PurchaseOrderSetMasksAction,
  SetPurchaseOrderAsyncErrorsAction,
  TopLocationPurchaseOrderActionTypes,
} from '../../actions/purchase-order-actions/purchase-order.actions';
import { PurchaseOrderMaskFormat, PurchaseOrderService, PurchaseOrderValidationResponseV8 } from '@cemex-core/cmx-api';
import { PurchaseOrderState } from '../../reducers/top-location-form.reducer';
import { purchaseOrderMasksConvert } from '@ordersnproducts/modules/shared/utils/purchase-order-masks-convert';
import { getPurchaseOrderValidationData, getPurchaseOrderValidationLocationData } from '../../selectors';
import { UnicEffectsAvailableEnum } from '@ordersnproducts/modules/shared/modules/dynamic-effects/deletable-effect-sources.constant';
import { EffectsRegistrationService } from '@ordersnproducts/modules/shared/modules/dynamic-effects/effects-registration.service';

/** Triggers the update for mandatory purchaseOrder validation */
@Injectable()
export class PurchaseOrderRMSMandatoryEffects {
  JobsiteLoadSalesAreaSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_SALES_AREA_SUCCESS),
      filter(() => this.isEffectAvailable()),
      switchMap((payload: LoadJobsiteSalesAreaComplete) => {
        return this.store.select(getPurchaseOrderValidationLocationData).pipe(
          filter((state) => !!state.jobsiteId && !!state.productLine),
          take(1),
          map((state) => {
            return new PurchaseOrderMandatoryAction(payload.payload[0].purchaseOrderValidation);
          }),
        );
      }),
    ),
  );

  /** Triggers obtaining valid purchaseOrder masks for user prompt and state update */

  PurchaseOrderMandatory$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TopLocationPurchaseOrderActionTypes.MANDATORY_PO),
      switchMap((action: PurchaseOrderMandatoryAction) => {
        if (action.payload) {
          return this.store.select(getPurchaseOrderValidationData).pipe(
            filter((state) => !!state.jobsiteId && !!state.productLine),
            take(1),
            switchMap((state) => {
              const payload = { jobsiteId: state.jobsiteId, productLine: state.productLine };
              return this.purchaseOrderService.getPOMasks(payload).pipe(
                map((response: PurchaseOrderValidationResponseV8) => {
                  const rawFormats = [];
                  response.formats.forEach((f: PurchaseOrderMaskFormat) => {
                    rawFormats.push(f.format);
                  });
                  return new PurchaseOrderSetMasksAction(purchaseOrderMasksConvert(rawFormats));
                }),
                catchError((error) => of(new SetPurchaseOrderAsyncErrorsAction({ obtainingMasksError: error }))),
              );
            }),
          );
        }

        return of(new PurchaseOrderSetMasksAction([]));
      }),
    ),
  );

  /**
   * Identifies if the effect is available to be executed
   * @returns {boolean}
   */
  private isEffectAvailable(): boolean {
    return this.effectRegister.isEffectAvailable(UnicEffectsAvailableEnum.PurchaseOrderRMSMandatoryEffects);
  }

  constructor(
    private actions$: Actions,
    private store: Store<PurchaseOrderState>,
    private purchaseOrderService: PurchaseOrderService,
    private effectRegister: EffectsRegistrationService,
  ) {}
}
